import React from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Modal, Spinner, Button } from 'fpay-ui';
import { useSelector } from 'react-redux';
import RefundCard from '@components/RefundCard';
import PdfRefund from '@components/PdfRefund';
import { useBreakpoint, useTransaction } from '@hooks';
import { saveReactToPdf } from '@helpers/exportPdf';

const Container = styled.div`
  ${({ isLoading }) =>
    isLoading &&
    `
    height: 100%;
    justify-content: center;
    > div {
      margin-top: 0px;
    }
  `}

  display: flex;
  flex-direction: column;
  padding: 16px 20px 16px 20px;
  img {
    width: 100px;
    margin: 0 auto;
  }
  h4 {
    font-weight: normal;
    margin-top: 16px;
    margin-bottom: 0px;
    font-size: 20px;
    line-height: 24px;
    text-align: center;
    color: ${({ theme }) => theme.colors.gray800};
  }
  p {
    margin-top: 13px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: ${({ theme }) => theme.colors.gray700};
  }
  > div {
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-top: 24px;
    ${({ theme }) => theme.breakpoint('md')} {
      flex-direction: row-reverse;
      button {
        :first-child {
          margin-left: 20px;
        }
      }
    }
    button {
      min-width: 120px;
      text-align: center;
    }
  }
`;

const StyledSpinner = styled(Spinner)`
  > p {
    margin-top: 0px;
  }
`;

const DownloadButton = styled(Button)`
  width: 131px;
  height: 40px;
  align-self: center;
  padding: 8px 14px;
  text-align: center;
  margin-top: 24px;
  ${({ theme }) => theme.breakpoint('md')} {
    margin-top: 16px;
  }
`;

const Title = styled.span(
  ({ theme }) => `
    margin: 0px;
    font-family: Roboto;
    font-size: 16px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.31;
    letter-spacing: 0.32px;
    text-align: left;
    color: ${theme.colors.gray700};
  `
);

const RefundModal = ({ fClose }) => {
  const { t } = useTranslation();
  const { mobile } = useBreakpoint();
  const { isLoading } = useSelector((state) => state.transactions);
  const ref = React.createRef();

  const { currentRefund } = useTransaction();

  const fileName = `refund-of-${
    currentRefund ? currentRefund?.transactionOriginal?.authorization_id : 'transaction'
  }.pdf`;

  const handleDownload = () => saveReactToPdf(ref, { fileName, x: 30 });

  return (
    <Modal
      showCloseButton={!isLoading}
      fClose={fClose}
      minHeight="509px"
      width={mobile ? '312px' : '344px'}
    >
      {isLoading ? (
        <Container isLoading={isLoading}>
          <StyledSpinner />
        </Container>
      ) : (
        <Container>
          <Title data-testid="refund-modal-title">{t('transactions.refunds.details.title')}</Title>
          <RefundCard marginTop="0px" detailAlwaysOpen />
          <DownloadButton onClick={handleDownload} testId="button-download">
            {t('common.download')}
          </DownloadButton>
          <PdfRefund refElement={ref} />
        </Container>
      )}
    </Modal>
  );
};

export default RefundModal;
